(function () {
  if (window.customElements && customElements.get('gallery-with-thumbnails')) return;

  var REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  class GalleryWithThumbnails extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.slides = Array.prototype.slice.call(this.querySelectorAll('.gallery-with-thumbnails__slide'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('.gallery-with-thumbnails__thumb'));
      this.cards = Array.prototype.slice.call(this.querySelectorAll('.gallery-with-thumbnails__card'));
      this.count = this.slides.length;
      if (this.count === 0) return;

      this.tablist = this.querySelector('.gallery-with-thumbnails__thumbs');
      this.progressBar = this.querySelector('[data-gallery-progress]');
      this.cardsEl = this.querySelector('.gallery-with-thumbnails__cards');

      this.index = Math.max(
        0,
        this.slides.findIndex(function (slide) {
          return slide.classList.contains('is-active');
        }),
      );

      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.autoplay = this.dataset.autoplay === 'true' && this.count > 1 && !REDUCE_MOTION.matches;
      this.style.setProperty('--tp-interval', this.interval + 'ms');

      this.trackCardHeight();

      this.pauseReasons = Object.create(null);
      this.remaining = this.interval;
      this.timer = null;
      this.startTime = 0;

      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('mouseenter', this.handleThumbSelect.bind(this));
        thumb.addEventListener('focus', this.handleThumbSelect.bind(this));
        thumb.addEventListener('click', this.handleThumbSelect.bind(this));
      }, this);

      if (this.tablist) {
        this.tablist.addEventListener('keydown', this.handleKeydown.bind(this));
      }

      this.render(this.index);

      if (this.autoplay) {
        this.classList.add('gallery-with-thumbnails__gallery--autoplay');

        this.addEventListener(
          'pointerenter',
          function () {
            this.pause('hover');
          }.bind(this),
        );
        this.addEventListener(
          'pointerleave',
          function () {
            this.resume('hover');
          }.bind(this),
        );
        this.addEventListener(
          'focusin',
          function () {
            this.pause('focus');
          }.bind(this),
        );
        this.addEventListener(
          'focusout',
          function (event) {
            if (!this.contains(event.relatedTarget)) this.resume('focus');
          }.bind(this),
        );

        this.onVisibilityChange = function () {
          if (document.hidden) this.pause('hidden');
          else this.resume('hidden');
        }.bind(this);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) this.resume('offscreen');
                else this.pause('offscreen');
              }, this);
            }.bind(this),
            { threshold: 0.2 },
          );
          this.observer.observe(this);
        } else {
          this.start();
        }
      }

      this.onBlockSelect = function (event) {
        var match = this.slides.filter(function (slide) {
          return slide.dataset.blockId === event.detail.blockId;
        })[0];
        if (!match) return;
        this.pause('editor');
        this.select(parseInt(match.dataset.index, 10), { silent: true });
      }.bind(this);
      this.onBlockDeselect = function () {
        this.resume('editor');
      }.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    trackCardHeight() {
      if (!this.cardsEl) return;

      var publish = function () {
        this.style.setProperty('--tp-card-height', this.cardsEl.offsetHeight + 'px');
      }.bind(this);

      publish();

      if (window.ResizeObserver) {
        this.cardResizeObserver = new ResizeObserver(publish);
        this.cardResizeObserver.observe(this.cardsEl);
      } else {
        this.onCardResize = publish;
        window.addEventListener('resize', this.onCardResize);
      }
    }

    disconnectedCallback() {
      this._initialised = false;
      this.stop();
      if (this.cardResizeObserver) this.cardResizeObserver.disconnect();
      if (this.onCardResize) window.removeEventListener('resize', this.onCardResize);
      if (this.observer) this.observer.disconnect();
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    select(index, options) {
      options = options || {};
      if (this.count === 0) return;
      index = ((index % this.count) + this.count) % this.count;
      this.index = index;
      this.render(index, options.focus);

      if (!options.silent && this.autoplay) {
        this.remaining = this.interval;
        if (!this.isPaused()) this.start();
      }
    }

    render(index, focus) {
      this.slides.forEach(function (slide, i) {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });

      this.thumbs.forEach(function (thumb, i) {
        var active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
        thumb.tabIndex = active ? 0 : -1;
      });

      this.cards.forEach(function (card, i) {
        var active = i === index;
        card.classList.toggle('is-active', active);
        if (active) card.removeAttribute('hidden');
        else card.setAttribute('hidden', '');
      });

      if (focus && this.thumbs[index]) this.thumbs[index].focus();
    }

    handleThumbSelect(event) {
      var index = parseInt(event.currentTarget.dataset.index, 10);
      if (isNaN(index) || index === this.index) return;
      this.select(index);
    }

    handleKeydown(event) {
      var last = this.count - 1;
      var index;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          index = this.index >= last ? 0 : this.index + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          index = this.index <= 0 ? last : this.index - 1;
          break;
        case 'Home':
          index = 0;
          break;
        case 'End':
          index = last;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.select(index, { focus: true });
    }

    start() {
      if (!this.autoplay) return;
      this.classList.remove('is-paused');
      this.classList.add('is-playing');
      this.remaining = this.interval;
      this.restartProgress();
      this.schedule(this.interval);
    }

    schedule(duration) {
      window.clearTimeout(this.timer);
      this.startTime = now();
      this.timer = window.setTimeout(this.advance.bind(this), duration);
    }

    advance() {
      this.select((this.index + 1) % this.count, { silent: true });
      this.remaining = this.interval;
      this.restartProgress();
      this.schedule(this.interval);
    }

    pause(reason) {
      this.pauseReasons[reason] = true;
      if (this.timer == null) return;
      window.clearTimeout(this.timer);
      this.timer = null;
      this.remaining = Math.max(0, this.remaining - (now() - this.startTime));
      this.classList.add('is-paused');
    }

    resume(reason) {
      delete this.pauseReasons[reason];
      if (!this.autoplay || this.isPaused() || this.timer != null) return;
      this.classList.remove('is-paused');
      this.classList.add('is-playing');
      this.schedule(this.remaining > 0 ? this.remaining : this.interval);
    }

    stop() {
      window.clearTimeout(this.timer);
      this.timer = null;
      this.classList.remove('is-playing');
    }

    isPaused() {
      return Object.keys(this.pauseReasons).length > 0;
    }

    restartProgress() {
      if (!this.autoplay) return;

      var bars = [];
      var thumb = this.thumbs[this.index];
      if (thumb) {
        var thumbBar = thumb.querySelector('.gallery-with-thumbnails__thumb-progress');
        if (thumbBar) bars.push(thumbBar);
      }
      if (this.progressBar) bars.push(this.progressBar);

      bars.forEach(function (bar) {
        bar.style.animation = 'none';
        void bar.offsetWidth;
        bar.style.animation = '';
      });
    }
  }

  customElements.define('gallery-with-thumbnails', GalleryWithThumbnails);
})();
